import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineRequisitionPurpose } from 'api/types';

/**
 * Requisition purposes (2026-10-26): the reasons a department can ask for non-chargeable food and drink,
 * as the tenant names them (Staff meal, Welcome drink, Management, Guest relations…). Opened from the
 * Requisitions toolbar with the `purposes` action.
 *
 * A purpose is never deleted: requisitions and the register keep its code. It is made inactive instead,
 * which takes it off the purpose picker for new requisitions. The code is fixed once added; the name,
 * order and active flag can change at any time.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  /** After any save, so the screen's purpose filter and list pick up the change. */
  onChanged: () => void;
}

interface Draft { id: string | null; code: string; name: string; order: string; active: boolean; rowVersion: number }

const EMPTY: Draft = { id: null, code: '', name: '', order: '', active: true, rowVersion: 0 };

/** A code from a name: "Guest relations" -> GUEST_RELATIONS, as the server stores it. */
const codeFrom = (name: string) => name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 20);

const DineOpsRequisitionPurposesModal: React.FC<Props> = ({ isOpen, onClose, onChanged }) => {
  const [purposes, setPurposes] = useState<DineRequisitionPurpose[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [codeTouched, setCodeTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPurposes(await dineOpsService.getRequisitionPurposes(true));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The purposes could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setDraft(EMPTY); setCodeTouched(false); setError(null);
    void load();
  }, [isOpen, load]);

  const edit = (p: DineRequisitionPurpose) => {
    setError(null);
    setDraft({ id: p.id, code: p.purposeCode, name: p.purposeName, order: String(p.displayOrder), active: p.isActive, rowVersion: p.rowVersion });
  };

  const save = useCallback(async (d: Draft) => {
    if (!d.name.trim()) { setError('Give the purpose a name.'); return; }
    setSaving(true); setError(null);
    const body = {
      purposeCode: d.code, purposeName: d.name.trim(), displayOrder: Number(d.order) || 0, isActive: d.active, rowVersion: d.rowVersion,
    };
    try {
      if (d.id) await dineOpsService.updateRequisitionPurpose(d.id, body);
      else await dineOpsService.createRequisitionPurpose(body);
      notificationService.toast.success(d.id ? `${body.purposeName} saved.` : `${body.purposeName} added.`);
      setDraft(EMPTY); setCodeTouched(false);
      await load();
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The purpose could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [load, onChanged]);

  /** Deactivate or reactivate straight from the row, keeping its name and order. */
  const toggle = (p: DineRequisitionPurpose) => void save({
    id: p.id, code: p.purposeCode, name: p.purposeName, order: String(p.displayOrder), active: !p.isActive, rowVersion: p.rowVersion,
  });

  if (!isOpen) return null;
  const editing = draft.id !== null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto', maxWidth: 680 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Requisition purposes</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>

        <div className="modal-content" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
            The reasons a department can ask for non-chargeable (NC) food and drink. The purpose prints on the NC KOT and
            groups the Requisition Register. A purpose in use is never deleted: make it inactive to stop offering it.
          </div>

          {error && <div className="form-error-message" style={{ marginBottom: 10 }}>{error}</div>}

          <div className="form-grid-3" style={{ alignItems: 'end' }}>
            <div className="form-group">
              <label className="form-label">{editing ? 'Rename' : 'New purpose'} <span className="text-danger">*</span></label>
              <input className="form-input" value={draft.name} maxLength={100} placeholder="e.g. Guest relations" disabled={saving}
                onChange={(e) => {
                  const name = e.target.value;
                  setDraft((d) => ({ ...d, name, code: editing || codeTouched ? d.code : codeFrom(name) }));
                }} />
            </div>
            <div className="form-group">
              <label className="form-label">Code</label>
              <input className="form-input" value={draft.code} maxLength={20} disabled={saving || editing}
                title={editing ? 'Fixed once added: requisitions keep it' : 'Filled from the name; change it if you like'}
                onChange={(e) => { setCodeTouched(true); setDraft((d) => ({ ...d, code: e.target.value.toUpperCase() })); }} />
            </div>
            <div className="form-group">
              <label className="form-label">Order</label>
              <input className="form-input" type="number" min={0} max={9999} value={draft.order} disabled={saving}
                onChange={(e) => setDraft((d) => ({ ...d, order: e.target.value }))} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 14 }}>
            {editing && <button className="btn btn-secondary btn-sm" onClick={() => setDraft(EMPTY)} disabled={saving}>Cancel edit</button>}
            <button className="btn btn-primary btn-sm" onClick={() => void save(draft)} disabled={saving || !draft.name.trim()}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Add purpose'}
            </button>
          </div>

          <table className="table" style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>Purpose</th>
                <th style={{ textAlign: 'left' }}>Code</th>
                <th style={{ textAlign: 'right' }}>Order</th>
                <th style={{ textAlign: 'right' }}>Used</th>
                <th style={{ textAlign: 'left' }}>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && purposes.length === 0 && <tr><td colSpan={6} style={{ opacity: 0.7 }}>Loading…</td></tr>}
              {purposes.map((p) => (
                <tr key={p.id} style={{ opacity: p.isActive ? 1 : 0.6 }}>
                  <td>{p.purposeName}</td>
                  <td style={{ fontFamily: 'monospace' }}>{p.purposeCode}</td>
                  <td style={{ textAlign: 'right' }}>{p.displayOrder}</td>
                  <td style={{ textAlign: 'right' }} title="Requisitions raised with it">{p.requisitionCount}</td>
                  <td><span className={`badge ${p.isActive ? 'badge-active' : 'badge-inactive'}`}>{p.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => edit(p)} disabled={saving}>Edit</button>{' '}
                    <button className="btn btn-secondary btn-sm" onClick={() => toggle(p)} disabled={saving}>
                      {p.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Close</button>
        </div>
      </div>
    </>
  );
};

export default DineOpsRequisitionPurposesModal;
